/** Session key written after checkout; consumed by My Facility to select the new row. */
export const POST_PURCHASE_FACILITY_SELECTION_KEY = "post_purchase_facility_selection";

/** My Facility tab shown immediately after checkout (not Facility Analysis). */
export const POST_PURCHASE_DEFAULT_ACTIVE_TAB = "dashboard" as const;

export type PostPurchaseFacilitySelection = {
  facilityId?: number;
  facilityName?: string;
  facilityCity?: string;
  /** Purchased facility vertical slug (aviation, food_service, …). */
  vertical?: string;
};

export function readPostPurchaseFacilitySelection(): PostPurchaseFacilitySelection | null {
  try {
    const raw = sessionStorage.getItem(POST_PURCHASE_FACILITY_SELECTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const facilityId =
      typeof parsed?.facilityId === "number" && Number.isFinite(parsed.facilityId)
        ? parsed.facilityId
        : undefined;
    const facilityName =
      typeof parsed?.facilityName === "string" && parsed.facilityName.trim()
        ? parsed.facilityName.trim()
        : undefined;
    const facilityCity =
      typeof parsed?.facilityCity === "string" && parsed.facilityCity.trim()
        ? parsed.facilityCity.trim()
        : undefined;
    const vertical =
      typeof parsed?.vertical === "string" && parsed.vertical.trim()
        ? parsed.vertical.trim().toLowerCase()
        : undefined;
    if (facilityId == null && !facilityName) return null;
    return { facilityId, facilityName, facilityCity, vertical };
  } catch {
    return null;
  }
}

export function clearPostPurchaseFacilitySelection(): void {
  try {
    sessionStorage.removeItem(POST_PURCHASE_FACILITY_SELECTION_KEY);
  } catch {
    /* ignore */
  }
}

/** Post-checkout navigation should land on Dashboard, not Facility Analysis. */
export function resolvePostPurchaseNavigationActiveTab(
  requestedTab: string | undefined,
  selection: PostPurchaseFacilitySelection | null,
): string {
  if (selection?.facilityId != null || selection?.facilityName) {
    return POST_PURCHASE_DEFAULT_ACTIVE_TAB;
  }
  return requestedTab ?? POST_PURCHASE_DEFAULT_ACTIVE_TAB;
}

/**
 * Views that must not be overridden by a pending post-purchase selection
 * (user is still in checkout / verification / setup).
 */
const POST_PURCHASE_VIEW_BLOCKLIST = new Set([
  "pricing",
  "verificationPending",
  "verificationFailed",
  "facilityValidation",
  "facilityRestricted",
  "facilityInput",
]);

/**
 * After checkout, `/dashboard/facilities` defaults to Regulations Library
 * (`spaces`) when router state is missing. If session still has a post-purchase
 * target, open Active Facility (`MyFacility`) instead so selection can resolve.
 */
export function preferMyFacilityViewForPostPurchase(
  routeCurrentView: string | null | undefined,
): boolean {
  if (!readPostPurchaseFacilitySelection()) return false;
  if (routeCurrentView && POST_PURCHASE_VIEW_BLOCKLIST.has(routeCurrentView)) {
    return false;
  }
  return !routeCurrentView || routeCurrentView === "spaces";
}
